import React, { memo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Assistant answers as real Markdown — the same `react-markdown` + `remark-gfm`
 * setup `MarkdownEditorModal` uses (no second Markdown stack, no raw HTML: none
 * of the rehype-raw family is enabled, and react-markdown's default URL
 * transform drops `javascript:` links).
 *
 * Direction: every block is `dir="auto"`, so a Hebrew answer in an English UI
 * aligns right (and vice versa); code stays LTR and isolated inside RTL text.
 * Styling is Tailwind descendant classes (no stylesheet import), so it works in
 * the extension's shadow roots, which only see the global stylesheet.
 */

type HastNode = { type: string; value?: string; children?: HastNode[] };

/** A hast subtree's text, in order. */
const hastText = (node?: HastNode): string =>
  !node ? '' : node.type === 'text' ? node.value ?? '' : (node.children ?? []).map(hastText).join('');

/**
 * The direction of a list from its first strong letter. `dir="auto"` can't do it:
 * it skips descendants that set their own `dir`, and every `<li>` does.
 */
const listDir = (node?: HastNode): 'rtl' | 'ltr' => {
  const first = hastText(node).match(/[A-Za-zÀ-ɏ֐-ࣿ]/)?.[0];
  return first && /[֐-ࣿ]/.test(first) ? 'rtl' : 'ltr';
};

const components: Components = {
  p: ({ node: _n, ...props }) => <p dir="auto" {...props} />,
  // Lists take their direction from their text too, so the start padding (and the
  // bullets it holds) lands on the right side for RTL — never clipped at the edge.
  ul: ({ node, ...props }) => <ul dir={listDir(node as HastNode)} {...props} />,
  ol: ({ node, ...props }) => <ol dir={listDir(node as HastNode)} {...props} />,
  li: ({ node: _n, ...props }) => <li dir="auto" {...props} />,
  h1: ({ node: _n, children, ...props }) => (
    <h1 dir="auto" {...props}>
      {children}
    </h1>
  ),
  h2: ({ node: _n, children, ...props }) => (
    <h2 dir="auto" {...props}>
      {children}
    </h2>
  ),
  h3: ({ node: _n, children, ...props }) => (
    <h3 dir="auto" {...props}>
      {children}
    </h3>
  ),
  h4: ({ node: _n, children, ...props }) => (
    <h4 dir="auto" {...props}>
      {children}
    </h4>
  ),
  blockquote: ({ node: _n, ...props }) => <blockquote dir="auto" {...props} />,
  td: ({ node: _n, ...props }) => <td dir="auto" {...props} />,
  th: ({ node: _n, ...props }) => <th dir="auto" {...props} />,
  pre: ({ node: _n, ...props }) => <pre dir="ltr" {...props} />,
  code: ({ node: _n, ...props }) => <code dir="ltr" {...props} />,
  a: ({ node: _n, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ node: _n, ...props }) => (
    <div className="ai-md-table overflow-x-auto">
      <table {...props} />
    </div>
  ),
};

const PROSE = [
  'ai-markdown break-words',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:my-3',
  '[&_h1]:mt-5 [&_h1]:mb-2 [&_h1]:text-[1.25em] [&_h1]:font-semibold [&_h1]:tracking-tight',
  '[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-[1.125em] [&_h2]:font-semibold',
  '[&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:font-semibold [&_h4]:mt-4 [&_h4]:mb-1 [&_h4]:font-semibold',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:ps-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:ps-6',
  '[&_li]:my-1 [&_li>p]:my-1',
  '[&_strong]:font-semibold',
  '[&_a]:underline [&_a]:underline-offset-2 [&_a]:text-ai-accent',
  '[&_code]:font-mono [&_code]:text-[0.875em] [&_code]:rounded-md [&_code]:px-1.5 [&_code]:py-0.5',
  '[&_code]:bg-ai-muted [&_code]:[unicode-bidi:isolate]',
  '[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:p-4 [&_pre]:text-start',
  '[&_pre]:bg-ai-muted [&_pre]:border [&_pre]:border-ai-border',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_blockquote]:my-3 [&_blockquote]:border-s-[3px] [&_blockquote]:ps-4',
  '[&_blockquote]:border-ai-border-strong [&_blockquote]:text-ai-text-secondary',
  '[&_hr]:my-5 [&_hr]:border-ai-border',
  '[&_.ai-md-table]:my-3 [&_table]:border-collapse [&_table]:text-[0.95em]',
  '[&_th]:border [&_td]:border [&_th]:px-3 [&_td]:px-3 [&_th]:py-1.5 [&_td]:py-1.5 [&_th]:font-semibold [&_th]:text-start',
  '[&_th]:border-ai-border-strong [&_td]:border-ai-border-strong',
].join(' ');

export const AiMarkdown = memo(function AiMarkdown({ text, className }: { text: string; className?: string }) {
  return (
    <div className={className ? `${PROSE} ${className}` : PROSE}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
});
